// src/lib/http/request.js
// Filepath: // src/lib/http/request.js

import { ApiError, UnauthorizedError, TimeoutError, NetworkError } from "./errors";
import { getAuthToken, clearAuth } from "../storage/authStorage";

/**
 * Fetch JSON with robust defaults, auth, and timeout.
 * Designed for TV WebView environments. AbortController optional.
 */

/** @typedef {Object} RequestJsonOptions
 * @property {'GET'|'POST'|'PUT'|'PATCH'|'DELETE'} [method]
 * @property {Object} [headers]
 * @property {any} [body]              // Will be JSON.stringify'ed if provided
 * @property {number} [timeoutMs]      // default 12000
 * @property {boolean} [withAuth]      // default false (adds Bearer if true)
 * @property {string}  [tokenOverride] // optional custom token
 */

export async function requestJson(url, {
  method = "GET",
  headers = {},
  body,
  timeoutMs = 12000,
  withAuth = false,
  tokenOverride
} = {}) {
  if (!url) {
    throw new ApiError("Missing URL for requestJson()");
  }

  const finalHeaders = {
    Accept: "application/json",
    "X-Client": "kbean-tv-app",
    ...headers,
  };

  // Only include Content-Type when a body is sent (friendlier for strict GET endpoints).
  const hasBody = body !== undefined;
  if (hasBody && !finalHeaders["Content-Type"]) {
    finalHeaders["Content-Type"] = "application/json";
  }

  // Auth header
  if (withAuth) {
    const token = tokenOverride ?? getAuthToken();
    if (!token) {
      throw new UnauthorizedError("Missing auth token", { url });
    }
    finalHeaders.Authorization = `Bearer ${token}`;
  }

  // Build fetch options
  const opts = {
    method,
    headers: finalHeaders,
  };
  if (hasBody) {
    opts.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  // Timeout with optional AbortController
  let controller = null;
  let timeoutId = null;
  if (typeof AbortController !== "undefined") {
    controller = new AbortController();
    opts.signal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    const res = await fetch(url, opts);

    if (timeoutId) clearTimeout(timeoutId);

    // Parse JSON safely (even on errors)
    let parsed = null;
    const text = await res.text();
    try {
      parsed = text ? JSON.parse(text) : null;
    } catch {
      parsed = text || null;
    }

    if (!res.ok) {
      if (res.status === 401) {
        // Clear stored auth so the app can re-pair or sign in again
        clearAuth();
        throw new UnauthorizedError("401 Unauthorized", { status: 401, url, body: parsed });
      }
      throw new ApiError(`HTTP ${res.status}`, { status: res.status, url, body: parsed });
    }

    return parsed;
  } catch (err) {
    if (err?.name === "AbortError") {
      throw new TimeoutError("Request timed out", { url });
    }
    if (err instanceof ApiError || err instanceof UnauthorizedError || err instanceof TimeoutError) {
      throw err;
    }
    throw new NetworkError(err?.message || "Network error", { url });
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}
